import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl, resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { editorialImageStyle } from "@/lib/editorialReaderLayout.mjs";
import { MediaLibraryPickerV2, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialCropV2 } from "./EditorialCropV2";
import { PublicationDestinationV2 } from "./PublicationDestinationV2";

const CROP_ASPECT = "4/5";
const MAIN_FRAME_LABEL = "Основной портрет · 4:5";
const MOBILE_FRAME_LABEL = "Мобильный портрет · 4:5";
const BG = "#EFE6D5";
const EYEBROW = "#8A6A46";
const TEXT = "#2A1D14";
const INTRO = "#5B4326";
const ACCENT = "#A67B4A";
const QUOTE = "#3E2E20";

function normalizeImage(raw) {
  if (!raw || (!raw.media_id && !raw.url)) return null;
  return {
    media_id: raw.media_id || null,
    url: raw.url || null,
    alt: raw.alt || "",
    caption: raw.caption || "",
    focal_x: raw.focal_x ?? 0.5,
    focal_y: raw.focal_y ?? 0.5,
    zoom: raw.zoom ?? 1,
  };
}

let idSeq = 0;
function newId() {
  idSeq += 1;
  return `qa-${Date.now().toString(36)}-${idSeq}`;
}

function deriveInitial(content) {
  const c = content || {};
  const p = c.portrait_dialogue && typeof c.portrait_dialogue === "object"
    ? c.portrait_dialogue
    : {};
  return {
    eyebrow: p.eyebrow ?? "",
    name: p.name ?? c.headline ?? "",
    role: p.role ?? "",
    intro: p.intro ?? c.lead ?? "",
    portrait: normalizeImage(p.portrait),
    mobilePortrait: normalizeImage(p.mobile_portrait),
    qa: Array.isArray(p.qa) && p.qa.length
      ? p.qa.map((q, i) => ({
          id: q?.id || `qa-legacy-${i}`,
          question: q?.question || "",
          answer: q?.answer || "",
        }))
      : [{ id: newId(), question: "", answer: "" }],
    quote: p.quote ?? c.quote ?? "",
    quoteSource: p.quote_source ?? c.signature ?? "",
    imageSide: p.image_side === "left" ? "left" : "right",
  };
}

function cleanQuote(value) {
  return String(value || "").trim().replace(/^[«"]+/, "").replace(/[»"]+$/, "").trim();
}

function PortraitImage({ image, maxHeight }) {
  if (!image?.url) return null;
  return (
    <div className="aspect-[4/5] overflow-hidden bg-[#E7DDCC]" style={{ maxHeight }}>
      <img
        src={resolveMediaUrl(image.url)}
        alt={image.alt || ""}
        className="h-full w-full"
        style={editorialImageStyle(image)}
      />
    </div>
  );
}

function QAPreview({ qa, mobile = false }) {
  const filled = qa.filter((q) => q.question?.trim() || q.answer?.trim());
  if (!filled.length) return null;
  return (
    <div className={mobile ? "mt-8 space-y-6" : "mt-10 space-y-8"}>
      {filled.map((q) => (
        <div key={q.id}>
          {q.question && (
            <div className="flex items-baseline gap-3">
              <span className="mt-1 shrink-0 text-[10px] uppercase tracking-[0.28em]" style={{ color: ACCENT }}>—&nbsp;В</span>
              <div className="font-serif leading-[1.3]" style={{ color: TEXT, fontSize: mobile ? "17px" : "19px" }}>
                {q.question}
              </div>
            </div>
          )}
          {q.answer && (
            <div className={(q.question ? "mt-3 " : "") + "flex items-baseline gap-3"}>
              <span className="mt-1 shrink-0 text-[10px] uppercase tracking-[0.28em]" style={{ color: INTRO }}>—&nbsp;О</span>
              <div className="max-w-[62ch] whitespace-pre-wrap font-serif text-[16px] leading-[1.75]" style={{ color: TEXT }}>
                {q.answer}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function QuotePreview({ quote, quoteSource, mobile = false }) {
  const q = cleanQuote(quote);
  if (!q) return null;
  return (
    <div className={(mobile ? "mt-8 " : "mt-10 ") + "border-l-2 pl-4"} style={{ borderColor: ACCENT }}>
      <div className="max-w-[56ch] font-serif italic leading-[1.5]" style={{ color: QUOTE, fontSize: mobile ? "18px" : "20px" }}>
        «{q}»
      </div>
      {quoteSource && (
        <div className="mt-2 text-[11px] uppercase tracking-[0.2em]" style={{ color: EYEBROW }}>
          — {quoteSource}
        </div>
      )}
    </div>
  );
}

function TextColumn({ eyebrow, name, role, intro, qa, quote, quoteSource, mobile = false }) {
  return (
    <div>
      {eyebrow && <div className="mb-4 text-[10px] uppercase tracking-[0.28em]" style={{ color: EYEBROW }}>{eyebrow}</div>}
      {name && <div className="font-serif leading-[1.08]" style={{ color: TEXT, fontSize: mobile ? "25px" : "30px" }}>{name}</div>}
      {role && <div className="mt-2 text-[11px] uppercase tracking-[0.24em]" style={{ color: EYEBROW }}>{role}</div>}
      {intro && <div className="mt-4 max-w-[56ch] font-serif text-[16px] italic leading-[1.6]" style={{ color: INTRO }}>{intro}</div>}
      <QAPreview qa={qa} mobile={mobile} />
      <QuotePreview quote={quote} quoteSource={quoteSource} mobile={mobile} />
    </div>
  );
}

function PhotoField({
  label, helper, image, uploading, onUpload, onLibrary, onCrop, onRemove, onAlt, onCaption, testidPrefix,
}) {
  return (
    <div>
      <label className="mb-1 block text-[11px] text-espresso/65">{label}</label>
      {helper && <p className="mb-2 text-xs text-espresso/50">{helper}</p>}
      {image?.url ? (
        <div className="flex items-start gap-3">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-espresso/10">
            <img
              src={resolveMediaUrl(image.url)}
              alt={image.alt || ""}
              className="h-full w-full object-cover"
              style={{ objectPosition: `${image.focal_x * 100}% ${image.focal_y * 100}%` }}
            />
          </div>
          <div className="flex-1 space-y-1.5">
            <input
              value={image.alt}
              onChange={(e) => onAlt(e.target.value)}
              placeholder="Описание изображения (alt)"
              className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
              data-testid={`${testidPrefix}-alt`}
            />
            <input
              value={image.caption}
              onChange={(e) => onCaption(e.target.value)}
              placeholder="Подпись под портретом"
              className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs"
              data-testid={`${testidPrefix}-caption`}
            />
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={onUpload} disabled={uploading}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso disabled:opacity-50">
                {uploading ? "Загрузка…" : "Заменить фото"}
              </button>
              <button type="button" onClick={onLibrary}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso">Из Медиатеки</button>
              <button type="button" onClick={onCrop}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso">Настроить кадр</button>
              <button type="button" onClick={onRemove}
                className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] text-espresso/70">Удалить фото</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={onUpload} disabled={uploading}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50">
            {uploading ? "Загрузка…" : "Загрузить фото"}
          </button>
          <button type="button" onClick={onLibrary}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso">Из Медиатеки</button>
        </div>
      )}
    </div>
  );
}

export function EditorialPortraitDialogueV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const initial = deriveInitial(c);

  const [eyebrow, setEyebrow] = useState(initial.eyebrow);
  const [name, setName] = useState(initial.name);
  const [role, setRole] = useState(initial.role);
  const [intro, setIntro] = useState(initial.intro);
  const [portrait, setPortrait] = useState(initial.portrait);
  const [mobilePortrait, setMobilePortrait] = useState(initial.mobilePortrait);
  const [qa, setQa] = useState(initial.qa);
  const [quote, setQuote] = useState(initial.quote);
  const [quoteSource, setQuoteSource] = useState(initial.quoteSource);
  const [imageSide, setImageSide] = useState(initial.imageSide);

  const [librarySlot, setLibrarySlot] = useState(null);
  const [cropSlot, setCropSlot] = useState(null);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [saveState, setSaveState] = useState("idle");
  const mainInput = useRef(null);
  const mobileInput = useRef(null);

  useEffect(() => {
    const next = deriveInitial(block.content || {});
    setEyebrow(next.eyebrow);
    setName(next.name);
    setRole(next.role);
    setIntro(next.intro);
    setPortrait(next.portrait);
    setMobilePortrait(next.mobilePortrait);
    setQa(next.qa);
    setQuote(next.quote);
    setQuoteSource(next.quoteSource);
    setImageSide(next.imageSide);
    setLibrarySlot(null);
    setCropSlot(null);
    setSaveState("idle");
  }, [block.id]);  const activeMobile = mobilePortrait?.url ? mobilePortrait : portrait;
  const cropImage = cropSlot === "mobile" ? mobilePortrait : cropSlot === "main" ? portrait : null;

  const addQa = () => setQa((rows) => [...rows, { id: newId(), question: "", answer: "" }]);
  const updateQa = (i, field, value) => setQa((rows) => rows.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  const removeQa = (i) => setQa((rows) => rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows);
  const moveQa = (i, dir) => setQa((rows) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return rows;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    const img = normalizeImage({ media_id: media.id, url });
    if (librarySlot === "mobile") setMobilePortrait(img);
    else setPortrait(img);
    setLibrarySlot(null);
  };

  const upload = async (file, slot) => {
    if (!file) return;
    const setUploading = slot === "mobile" ? setUploadingMobile : setUploadingMain;
    const setImage = slot === "mobile" ? setMobilePortrait : setPortrait;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setImage(normalizeImage({ media_id: data.id, url }));
      toast.success("Фото загружено");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const saveCrop = (values) => {
    if (cropSlot === "mobile") setMobilePortrait((img) => img ? { ...img, ...values } : img);
    if (cropSlot === "main") setPortrait((img) => img ? { ...img, ...values } : img);
    setCropSlot(null);
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        portrait_dialogue: {
          eyebrow,
          name,
          role,
          intro,
          portrait: portrait ? { ...portrait } : null,
          mobile_portrait: mobilePortrait ? { ...mobilePortrait } : null,
          qa: qa.map((q) => ({ id: q.id, question: q.question, answer: q.answer })),
          quote,
          quote_source: quoteSource,
          image_side: imageSide,
        },
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      const savedMediaItems = [portrait, mobilePortrait]
        .filter((img) => img?.media_id && img?.url)
        .map((img) => ({ id: img.media_id, url: img.url }));
      onSaved?.({ ...block, content: nextContent }, savedMediaItems);
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = { idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения" }[saveState];

  return (
    <div onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between border-b border-espresso/10 px-5 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Портрет / Диалог</div>
            <div className="mt-0.5 font-serif text-xl text-espresso">{name || "Портрет / Диалог"}</div>
          </div>
          {saveLabel && <span className="text-[11px] uppercase tracking-widest text-espresso/50">{saveLabel}</span>}
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <PublicationDestinationV2 materialId={block.material_id} volume={volume} />

          <PhotoField
            label="Основной портрет"
            image={portrait}
            uploading={uploadingMain}
            onUpload={() => mainInput.current?.click()}
            onLibrary={() => setLibrarySlot("main")}
            onCrop={() => setCropSlot("main")}
            onRemove={() => setPortrait(null)}
            onAlt={(v) => setPortrait((img) => img ? { ...img, alt: v } : img)}
            onCaption={(v) => setPortrait((img) => img ? { ...img, caption: v } : img)}
            testidPrefix="editorial-v2-portraitdialogue-main"
          />
          <input ref={mainInput} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, "main"); }} />

          <PhotoField
            label="Мобильный портрет · необязательно"
            helper="Если не добавлено, на телефоне используется основной портрет."
            image={mobilePortrait}
            uploading={uploadingMobile}
            onUpload={() => mobileInput.current?.click()}
            onLibrary={() => setLibrarySlot("mobile")}
            onCrop={() => setCropSlot("mobile")}
            onRemove={() => setMobilePortrait(null)}
            onAlt={(v) => setMobilePortrait((img) => img ? { ...img, alt: v } : img)}
            onCaption={(v) => setMobilePortrait((img) => img ? { ...img, caption: v } : img)}
            testidPrefix="editorial-v2-portraitdialogue-mobile"
          />
          <input ref={mobileInput} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f, "mobile"); }} />

          <div>
            <label className="mb-1 block text-[11px] text-espresso/60">Положение портрета на Desktop</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setImageSide("left")}
                className={`rounded-lg border px-3 py-2 text-xs ${imageSide === "left" ? "border-espresso bg-espresso text-cream" : "border-espresso/20 bg-white text-espresso"}`}>
                Портрет слева
              </button>
              <button type="button" onClick={() => setImageSide("right")}
                className={`rounded-lg border px-3 py-2 text-xs ${imageSide === "right" ? "border-espresso bg-espresso text-cream" : "border-espresso/20 bg-white text-espresso"}`}>
                Портрет справа
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <input value={eyebrow} onChange={(e) => setEyebrow(e.target.value)}
              placeholder="Метка раздела" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Имя героя" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <input value={role} onChange={(e) => setRole(e.target.value)}
              placeholder="Роль / должность" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={3}
              placeholder="Введение" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />

            <div>
              <label className="mb-1 block text-[11px] text-espresso/60">Вопросы и ответы</label>
              <div className="space-y-2">
                {qa.map((q, i) => (
                  <div key={q.id} className="flex gap-2 rounded-lg border border-espresso/15 p-2">
                    <div className="flex-1 space-y-1.5">
                      <input value={q.question} onChange={(e) => updateQa(i, "question", e.target.value)}
                        placeholder="Вопрос" className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs" />
                      <textarea value={q.answer} onChange={(e) => updateQa(i, "answer", e.target.value)}
                        rows={3} placeholder="Ответ героя" className="w-full rounded-lg border border-espresso/20 px-2 py-1 text-xs" />
                    </div>
                    <div className="flex shrink-0 flex-col gap-1">
                      <button type="button" onClick={() => moveQa(i, -1)} disabled={i === 0}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▲</button>
                      <button type="button" onClick={() => moveQa(i, 1)} disabled={i === qa.length - 1}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">▼</button>
                      <button type="button" onClick={() => removeQa(i)} disabled={qa.length <= 1}
                        className="rounded border border-espresso/20 bg-white px-2 py-1 text-[11px] disabled:opacity-30">Удалить</button>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addQa}
                className="mt-2 text-xs text-espresso/60 underline underline-offset-2">+ Добавить вопрос</button>
            </div>

            <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={2}
              placeholder="Авторская цитата" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
            <input value={quoteSource} onChange={(e) => setQuoteSource(e.target.value)}
              placeholder="Источник / подпись" className="w-full rounded-lg border border-espresso/20 px-3 py-2 text-sm" />
          </div>

          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white p-4">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-espresso/50">Предпросмотр разворота</div>
                <div className="flex gap-1 rounded-full border border-espresso/15 p-0.5 text-[11px]">
                  <button type="button" onClick={() => setPreviewDevice("desktop")}
                    className={previewDevice === "desktop" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>Desktop</button>
                  <button type="button" onClick={() => setPreviewDevice("mobile")}
                    className={previewDevice === "mobile" ? "rounded-full bg-espresso px-2.5 py-1 text-cream" : "rounded-full px-2.5 py-1 text-espresso/60"}>Mobile</button>
                </div>
              </div>

              {previewDevice === "desktop" ? (
                portrait?.url ? (
                  <div className="mx-auto p-4" style={{ maxWidth: "1200px", backgroundColor: BG }}>
                    <div className="grid gap-3 lg:gap-4" style={{ gridTemplateColumns: "minmax(0,40fr) minmax(0,60fr)" }}>
                      {imageSide === "left" ? (
                        <>
                          <div>
                            <PortraitImage image={portrait} maxHeight={680} />
                            {portrait.caption && <div className="mt-3 font-serif text-[12px] italic" style={{ color: INTRO }}>{portrait.caption}</div>}
                          </div>
                          <TextColumn eyebrow={eyebrow} name={name} role={role} intro={intro} qa={qa} quote={quote} quoteSource={quoteSource} />
                        </>
                      ) : (
                        <>
                          <TextColumn eyebrow={eyebrow} name={name} role={role} intro={intro} qa={qa} quote={quote} quoteSource={quoteSource} />
                          <div>
                            <PortraitImage image={portrait} maxHeight={680} />
                            {portrait.caption && <div className="mt-3 font-serif text-[12px] italic" style={{ color: INTRO }}>{portrait.caption}</div>}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto px-6 py-10" style={{ maxWidth: "760px", backgroundColor: BG }}>
                    <TextColumn eyebrow={eyebrow} name={name} role={role} intro={intro} qa={qa} quote={quote} quoteSource={quoteSource} />
                  </div>
                )
              ) : (
                <div className="mx-auto max-w-[240px] p-3" style={{ backgroundColor: BG }}>
                  {eyebrow && <div className="mb-3 text-[10px] uppercase tracking-[0.28em]" style={{ color: EYEBROW }}>{eyebrow}</div>}
                  {name && <div className="font-serif text-[25px] leading-[1.08]" style={{ color: TEXT }}>{name}</div>}
                  {role && <div className="mt-2 text-[11px] uppercase tracking-[0.24em]" style={{ color: EYEBROW }}>{role}</div>}
                  {activeMobile?.url && (
                    <div className="mt-6">
                      <PortraitImage image={activeMobile} maxHeight={420} />
                      {activeMobile.caption && <div className="mt-3 font-serif text-[12px] italic" style={{ color: INTRO }}>{activeMobile.caption}</div>}
                    </div>
                  )}
                  {intro && <div className="mt-4 font-serif text-[16px] italic leading-[1.6]" style={{ color: INTRO }}>{intro}</div>}
                  <QAPreview qa={qa} mobile />
                  <QuotePreview quote={quote} quoteSource={quoteSource} mobile />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">Закрыть</button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving"}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50">Сохранить изменения</button>
        </div>
      </div>

      <MediaLibraryPickerV2
        open={librarySlot !== null}
        onClose={() => setLibrarySlot(null)}
        onPick={pickFromLibrary}
      />

      <EditorialCropV2
        open={cropSlot !== null}
        url={resolveMediaUrl(cropImage?.url || "")}
        aspectRatio={CROP_ASPECT}
        frameLabel={cropSlot === "mobile" ? MOBILE_FRAME_LABEL : MAIN_FRAME_LABEL}
        focalX={cropImage?.focal_x ?? 0.5}
        focalY={cropImage?.focal_y ?? 0.5}
        zoom={cropImage?.zoom ?? 1}
        onSave={saveCrop}
        onClose={() => setCropSlot(null)}
      />
    </div>
  );
}
